import { FC, useState } from 'react'
import gql from 'graphql-tag'
import Link from 'next/link'
import styled from 'styled-components'
import { ArrowLeft, ArrowRight } from 'svgIcons'
import {
  BlankType,
  CharSize,
  SelfIntroductionDetailModal_SelfIntroductionFragment,
} from 'generated/graphql'
import Modal, { IFModalProps } from '@app.components/Modal/Modal'
import { formatBlankType } from '@app.feature/coverletter/coverletter.write/libs/utils'
import ExportButtonMobile from './ExportButtonMobile'

gql`
  fragment SelfIntroductionDetailModal_SelfIntroduction on SelfIntroduction {
    id
    title
    activity {
      id
    }
    qna {
      id
      question
      answer
      charSize
      charMaxSize
      blankType
      seq
    }
    ...StoreSelfIntroduction_SelfIntroduction
  }
`

interface SelfIntroductionDetailModalProps extends IFModalProps {
  data: SelfIntroductionDetailModal_SelfIntroductionFragment
}

const SelfIntroductionDetailModal: FC<SelfIntroductionDetailModalProps> = ({
  data,
  onClose,
  ...props
}) => {
  const [selectedQna, setSelectedQna] = useState(data.qna?.[0])

  return (
    <Modal onClose={onClose} animation="left" {...props}>
      <StyledWrapper>
        <div className="header">
          <ArrowLeft onClick={onClose} />
          <span>{data.title}</span>
          <ExportButtonMobile data={data} />
        </div>
        <div className="noti">
          자소서 작성 및 수정은 PC, 모바일 앱을 이용해주세요.
        </div>
        <div className="qna-list">
          {data.qna?.map((qna) => (
            <button
              key={qna.id}
              className="qna-num"
              data-active={qna.id === selectedQna?.id}
              onClick={() => setSelectedQna(qna)}
            >
              {String(qna.seq).padStart(2, '0')}
            </button>
          ))}
        </div>
        <div className="question" data-empty={!selectedQna?.question}>
          {selectedQna?.question || '입력한 자기소개서 질문이 없습니다.'}
        </div>
        <div className="answer" data-empty={!selectedQna?.answer}>
          {selectedQna?.answer || '입력한 자기소개서 내용이 없습니다.'}
        </div>
        <div className="footer">
          <div>
            <span>{selectedQna?.answer?.length}</span>/
            {selectedQna?.charMaxSize}
            {selectedQna?.charSize === CharSize.CHAR ? `자` : ` bytes`}(
            {formatBlankType(selectedQna?.blankType || BlankType.WITH_BLANK)})
          </div>
          {data.activity && (
            <Link href={`/activity/${data.activity.id}`}>
              <a className="activity-btn">
                공고보기
                <ArrowRight />
              </a>
            </Link>
          )}
        </div>
      </StyledWrapper>
    </Modal>
  )
}

export default SelfIntroductionDetailModal

const StyledWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0 20px;
  background-color: #fff;
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 50px;
    span {
      font-size: 16px;
      font-weight: bold;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding: 0 30px;
    }
    svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
  }
  .noti {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    font-size: 14px;
    font-weight: bold;
    margin-top: 10px;
    border-radius: 5px;
    color: #01a0ff;
    background-color: #f2faff;
  }
  .qna-list {
    display: flex;
    overflow-x: auto;
    gap: 5px;
    padding-top: 12px;
    -ms-overflow-style: none;
    scrollbar-width: none;
    ::-webkit-scrollbar {
      display: none;
    }
    .qna-num {
      line-height: 1.43;
      padding: 6px 12px 7px;
      font-size: 12px;
      font-weight: 500;
      border-radius: 15px;
      border: solid 1px #ccc;
      &[data-active='true'] {
        color: #fff;
        font-weight: bold;
        background-color: #01a0ff;
        border-color: #01a0ff;
      }
    }
  }
  .question {
    height: 66px;
    padding: 12px 15px;
    margin-top: 12px;
    border-radius: 5px;
    border: solid 1px #ccc;
    font-size: 14px;
    font-weight: 500;
    overflow-y: auto;
    &[data-empty='true'] {
      color: #999;
    }
  }
  .answer {
    height: 357px;
    margin-top: 12px;
    padding: 12px 15px;
    border-radius: 5px;
    border: solid 1px #ccc;
    font-size: 14px;
    overflow-y: auto;
    &[data-empty='true'] {
      color: #999;
    }
  }
  .footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 37px;
    padding: 0 15px;
    margin-top: 12px;
    border-radius: 5px;
    background-color: #f4f4f4;
    font-size: 12px;
    span {
      color: #01a0ff;
    }
  }
  .activity-btn {
    display: flex;
    align-items: center;
    gap: 3px;

    svg {
      width: 8px;
      height: 8px;
    }
  }
`
